/**
 * Launch timing: the "3… 2… 1… LET IT RIP!" power meter. A marker sweeps a bar
 * and the player locks it; how close to the sweet spot decides launch power,
 * which sets starting spin and launch speed. Pure + testable.
 */
import { SIM } from './config.ts';

export const LAUNCH = {
  /** Centre of the sweet spot on the 0..1 bar. */
  sweetCenter: 0.5,
  /** Half-width of the (full-power) sweet zone. */
  sweetHalf: 0.1,
  /** Worst-case launch power (a totally mistimed pull). */
  minPower: 0.55,
  /** Starting spin as a fraction of max, at min vs full power. */
  spinAtMin: 0.7,
  spinAtMax: 1.0,
  /** Launch speed multiplier at min vs full power. */
  speedAtMin: 0.75,
  speedAtMax: 1.25,
} as const;

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

/**
 * Launch power (0..1) from a locked marker position (0..1). Anywhere inside the
 * sweet zone is full power; outside it falls off linearly to `minPower` at the
 * ends of the bar.
 */
export function launchPower(pos: number): number {
  const dist = Math.abs(clamp(pos, 0, 1) - LAUNCH.sweetCenter);
  if (dist <= LAUNCH.sweetHalf) return 1;
  const t = (dist - LAUNCH.sweetHalf) / (0.5 - LAUNCH.sweetHalf);
  return clamp(lerp(1, LAUNCH.minPower, t), LAUNCH.minPower, 1);
}

/** Starting spin (absolute) for a given launch power. */
export function powerToSpin(power: number): number {
  return SIM.MAX_SPIN * lerp(LAUNCH.spinAtMin, LAUNCH.spinAtMax, clamp(power, 0, 1));
}

/** Launch-speed multiplier for a given launch power. */
export function powerToSpeedMult(power: number): number {
  return lerp(LAUNCH.speedAtMin, LAUNCH.speedAtMax, clamp(power, 0, 1));
}

/** A "perfect" launch sits inside the sweet zone. */
export const isPerfect = (pos: number): boolean =>
  Math.abs(clamp(pos, 0, 1) - LAUNCH.sweetCenter) <= LAUNCH.sweetHalf;

/** The AI's launch power, scaled by difficulty (with a little jitter). */
export function aiLaunchPower(difficulty: number, rng: () => number = Math.random): number {
  const base = lerp(0.62, 1.0, clamp(difficulty, 0, 1));
  return clamp(base + (rng() - 0.5) * 0.1, LAUNCH.minPower, 1);
}
